import 'alpine-turbo-drive-adapter'
import Alpine from 'alpinejs'
import Sortable from 'sortablejs'

window.Alpine = Alpine

// alpine-turbo-drive-adapter 2.2.0 tags x-for clones for removal before Turbo caches the page with
// `Object.values(el._x_lookup)`, which returns [] since Alpine 3.15.9 made `_x_lookup` a Map. The clones
// then stay in the cached snapshot, and after Back Alpine renders a second set next to them.
// Tag them here so the adapter's turbo:before-render handler strips them from the restored page.
// Registered at import, before initAlpine() calls Alpine.start(), so it runs ahead of the adapter's own
// turbo:before-cache handler, which untags clones inside [data-turbo-permanent] elements.
document.addEventListener('turbo:before-cache', () => {
  Alpine.mutateDom(() => {
    document.body.querySelectorAll('[x-for]').forEach((el) => {
      // eslint-disable-next-line no-underscore-dangle
      el._x_lookup?.forEach((clone) => clone.setAttribute('data-alpine-generated-me', true))
    })
  })
})

// Per-weekday shift row group on the agent edit page. Each weekday uses its
// own component instance so $refs scope to that day's list/template, but
// child_index seeds come from a single module-level counter so two weekday
// instances initialized in the same JS tick can't collide on Date.now().
let shiftFieldsNextIndex = Date.now()

/* eslint-disable sort-keys */
// Copies the live values of every rule[...] field from the main rule form into
// hidden inputs inside the preview-test form, so the preview submits the current
// (unsaved) form state. Used by ruleTestForm() on the rule edit/new page.
const copyRuleFieldsToContainer = (mainForm, hiddenContainer) => {
        const formData = new FormData(mainForm)
        for (const [key, value] of formData.entries()) {
          if (key.startsWith('rule[')) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.name = key
            input.value = value
            hiddenContainer.appendChild(input)
          }
        }
      },
      // Each condition group (root and every nested child) is its own instance, so
      // $refs scope to that group's own conditions list / child-group container.
      conditionGroupFields = () => ({
        nextIndex: 0,
        destroyed: false,
        init () {
          this.nextIndex = Date.now()
        },
        addCondition () {
          const html = this.$refs.conditionTemplate.innerHTML.replace(/__INDEX__/gu, this.nextIndex)
          this.nextIndex += 1
          this.$refs.conditionsList.insertAdjacentHTML('beforeend', html)
          this.$refs.conditionsList.dispatchEvent(new CustomEvent('condition:fields-added', { bubbles: true }))
        },
        addChildGroup () {
          const template = this.$refs.childGroupTemplate,
            container = this.$refs.childGroupsContainer,
            placeholder = template && template.dataset.placeholder,
            html = placeholder && template.innerHTML.replace(new RegExp(placeholder, 'gu'), Date.now())
          if (!template || !container) {
            return
          }
          container.insertAdjacentHTML('beforeend', html)
          container.dispatchEvent(new CustomEvent('condition:fields-added', { bubbles: true }))
        },
      }),
      shiftFields = () => ({
        addShift () {
          const html = this.$refs.template.innerHTML.replace(/__INDEX__/gu, shiftFieldsNextIndex)
          shiftFieldsNextIndex += 1 // eslint-disable-line no-magic-numbers
          this.$refs.list.insertAdjacentHTML('beforeend', html)
        },
      }),
      ruleReorder = () => ({
        sortable: null,
        init () {
          this.sortable = Sortable.create(this.$el, {
            animation: 300,
            handle: '.drag-handle',
            ghostClass: 'opacity-50',
            draggable: 'tbody',
            onEnd: () => {
              const tbodies = this.$el.querySelectorAll('tbody[data-rule-id]'),
                ruleIds = Array.from(tbodies, (el) => el.dataset.ruleId),
                csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

              tbodies.forEach((tbody, index) => {
                const label = tbody.querySelector('.rule-label')
                if (label) {
                  // eslint-disable-next-line no-magic-numbers
                  label.textContent = label.textContent.replace(/#\d+:/u, `#${index + 1}:`)
                }
              })

              fetch('/rules/reorder', {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRF-Token': csrfToken,
                },
                body: JSON.stringify({ 'rule_ids': ruleIds }),
              }).then((response) => {
                if (!response.ok) {
                  window.location.reload()
                }
              }).catch(() => {
                window.location.reload()
              })
            },
          })
        },
        destroy () {
          if (this.sortable) {
            this.sortable.destroy()
            this.sortable = null
          }
        },
      }),
      ruleTestForm = () => ({
        loading: false,
        prepareTestFields () {
          const mainForm = document.getElementById('rule-form')
          if (!mainForm) {
            return
          }
          this.$refs.hiddenFields.innerHTML = ''
          copyRuleFieldsToContainer(mainForm, this.$refs.hiddenFields)
        },
      })

export default function initAlpine () {
  Alpine.data('transitions', () => ({
    slideRightRevertTransition: {
      'x-transition:enter' () { return 'transition ease-in-out duration-300 transform' },
      'x-transition:enter-start' () { return '-translate-x-full' },
      'x-transition:enter-end' () { return 'translate-x-0' },
      'x-transition:leave' () { return 'transition ease-in-out duration-300 transform' },
      'x-transition:leave-start' () { return 'translate-x-0' },
      'x-transition:leave-end' () { return '-translate-x-full' },
    },
    zoomUpTransition: {
      'x-transition:enter' () { return 'transition ease-out duration-300' },
      'x-transition:enter-start' () { return 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95' },
      'x-transition:enter-end' () { return 'opacity-100 translate-y-0 sm:scale-100' },
      'x-transition:leave' () { return 'transition ease-in duration-200' },
      'x-transition:leave-start' () { return 'opacity-100 translate-y-0 sm:scale-100' },
      'x-transition:leave-end' () { return 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95' },
    },
    fadeOpacityTransition: {
      'x-transition:enter' () { return 'transition ease-linear duration-300' },
      'x-transition:enter-start' () { return 'opacity-0' },
      'x-transition:enter-end' () { return 'opacity-100' },
      'x-transition:leave' () { return 'transition ease-linear duration-300' },
      'x-transition:leave-start' () { return 'opacity-100' },
      'x-transition:leave-end' () { return 'opacity-0' },
    },
    notificationTransition: {
      'x-transition:enter' () { return 'transition ease-out duration-300 transform' },
      'x-transition:enter-start' () { return 'translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2' },
      'x-transition:enter-end' () { return 'translate-y-0 opacity-100 sm:translate-x-0' },
      'x-transition:leave' () { return 'transition ease-in-out duration-300 transform' },
      'x-transition:leave-start' () { return 'opacity-100' },
      'x-transition:leave-end' () { return 'opacity-0' },
    },
  }))

  window.runOnce = function runOnce (token, runnable) {
    if (!sessionStorage.getItem(token)) {
      sessionStorage.setItem(token, '-')
      return runnable()
    }
    return false
  }

  Alpine.data('conditionGroupFields', conditionGroupFields)
  Alpine.data('shiftFields', shiftFields)
  Alpine.data('ruleReorder', ruleReorder)
  Alpine.data('ruleTestForm', ruleTestForm)
  Alpine.start()
}
/* eslint-enable sort-keys */
